import { useEffect, useState } from 'react';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import {
  getQueueStatus,
  getQueueStats,
  triggerProcessing,
  getFailedJobs,
  retryBuild,
  type QueueStatusResponse,
  type QueueStatsResponse,
  type FailedQueueJob,
} from '../api/queue';
import { formatRelativeTime } from '../utils/formatters';

type JobStatus = 'queued' | 'generating' | 'completed' | 'failed';

const statusVariants: Record<JobStatus, 'default' | 'info' | 'success' | 'error'> = {
  queued: 'default',
  generating: 'info',
  completed: 'success',
  failed: 'error',
};

export function GenerationQueuePage() {
  const [queueStatus, setQueueStatus] = useState<QueueStatusResponse | null>(null);
  const [queueStats, setQueueStats] = useState<QueueStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedJobs, setFailedJobs] = useState<FailedQueueJob[]>([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryNote, setRetryNote] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const [statusData, statsData, failedData] = await Promise.all([
        getQueueStatus(),
        getQueueStats(),
        // Failures are fetched alongside live work rather than behind a tab.
        // A build that gave up is the thing most worth seeing on this page.
        getFailedJobs().catch(() => [] as FailedQueueJob[]),
      ]);
      setQueueStatus(statusData);
      setQueueStats(statsData);
      setFailedJobs(failedData);
      setError(null);
    } catch (err) {
      setError('Failed to load queue data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRetry = async (job: FailedQueueJob) => {
    setRetryingId(job.id);
    setRetryNote(null);
    try {
      const res = await retryBuild(job.website_id);
      setRetryNote(`${job.business_name || 'Site'}: ${res.message}`);
      await fetchData();
    } catch (err) {
      setRetryNote(
        `Couldn't re-queue ${job.business_name || 'that site'} — ${
          err instanceof Error ? err.message : 'please try again'
        }`
      );
    } finally {
      setRetryingId(null);
    }
  };

  const handleProcessQueue = async () => {
    setProcessing(true);
    try {
      await triggerProcessing();
      // Wait a moment then refresh
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await fetchData();
    } catch (err) {
      setError('Failed to trigger processing');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <PageContainer title="Generation Queue">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  // Calculate stats
  const statsByStatus = queueStats?.stats.reduce(
    (acc, stat) => {
      acc[stat.status] = stat;
      return acc;
    },
    {} as Record<string, { count: number; avg_generation_time: number | null }>
  ) || {};

  return (
    <PageContainer
      title="Generation Queue"
      subtitle="Monitor website generation jobs"
      action={
        <Button onClick={handleProcessQueue} loading={processing}>
          Process Queue
        </Button>
      }
    >
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Queued"
          value={statsByStatus['queued']?.count || 0}
          color="gray"
        />
        <StatCard
          label="Generating"
          value={statsByStatus['generating']?.count || 0}
          color="blue"
        />
        <StatCard
          label="Completed"
          value={statsByStatus['completed']?.count || 0}
          color="green"
        />
        <StatCard
          label="Failed"
          value={statsByStatus['failed']?.count || 0}
          color="red"
        />
      </div>

      {/* Average Generation Time */}
      {statsByStatus['completed']?.avg_generation_time && (
        <Card className="mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500">Average Generation Time</p>
              <p className="text-xl font-bold text-gray-900">
                {Math.round(statsByStatus['completed'].avg_generation_time)}s
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Active Jobs Table */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Active Jobs ({queueStatus?.length || 0})
        </h2>
        {queueStatus && queueStatus.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Business
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Queued
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Attempts
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {queueStatus.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {job.business_name}
                        </p>
                        <p className="text-sm text-gray-500">{job.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusVariants[job.status as JobStatus] || 'default'}>
                        {job.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${job.priority > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
                        {job.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatRelativeTime(job.queued_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {job.retry_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            No active jobs in the queue
          </p>
        )}
      </Card>

      {/* Failed builds — surfaced, with a way to act on them */}
      {failedJobs.length > 0 && (
        <Card className="mt-6 border-red-200">
          <div className="flex items-start justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Failed Builds ({failedJobs.length})
            </h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            These gave up after exhausting their retries. Nothing will pick them up again on its own.
          </p>
          {retryNote && (
            <div className="mb-4 px-3 py-2 rounded-md bg-blue-50 text-sm text-blue-800">
              {retryNote}
            </div>
          )}
          <div className="space-y-3">
            {failedJobs.map((job) => (
              <div
                key={job.id}
                className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {job.business_name || 'Untitled site'}
                    </span>
                    <Badge variant="error">failed</Badge>
                    {job.retry_count != null && (
                      <span className="text-xs text-gray-400">
                        {job.retry_count} {job.retry_count === 1 ? 'attempt' : 'attempts'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{job.email}</p>
                  {job.error_message && (
                    <p className="text-xs text-red-700 mt-1.5 font-mono break-all line-clamp-3">
                      {job.error_message}
                    </p>
                  )}
                  {job.completed_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Failed {formatRelativeTime(job.completed_at)}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={retryingId === job.id}
                  disabled={retryingId !== null}
                  onClick={() => handleRetry(job)}
                >
                  Retry build
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Completions */}
      {queueStats && queueStats.recent_completed.length > 0 && (
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Completions
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Business
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Generation Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Completed
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {queueStats.recent_completed.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {job.business_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {job.generation_time_seconds}s
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatRelativeTime(job.completed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="text-sm text-gray-500 text-center mt-6">
        Auto-refreshes every 30 seconds
      </p>
    </PageContainer>
  );
}

// Stat card component
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: 'gray' | 'blue' | 'green' | 'red';
}) {
  const colorStyles = {
    gray: 'bg-gray-100 text-gray-800',
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
  };

  return (
    <Card>
      <div className="text-center">
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${colorStyles[color]} inline-block px-4 py-1 rounded-full`}>
          {value}
        </p>
      </div>
    </Card>
  );
}
